import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { Icon, PriceDisplay } from "@b2bportal/core-ui";
import { toStarRating } from "@b2bportal/core-utilities";
import type { HotelItinerary } from "@b2bportal/lodging-api";
import {
  type LodgingCancelScenario,
  LodgingCancelScenarioEnum,
} from "@b2bportal/lodging-cancel-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import styles from "./styles.module.scss";
import { useReservationDateTime } from "@/utils/format/useReservationDateTime";
import { getLodgingAddress } from "@/utils/lodging";

interface HotelCancelCardProps {
  hotelReservation: HotelItinerary;
  cancelScenario: LodgingCancelScenario;
  refundAmount: {
    fiat: number;
    rewards: number;
  };
}

export const HotelCancelCard = ({
  hotelReservation,
  cancelScenario,
  refundAmount,
}: HotelCancelCardProps) => {
  const [block, cn] = useModuleBEM(styles, "HotelCancelCard");
  const { t, formatFiatCurrency } = useI18nContext();
  const hotelData = hotelReservation.reservation.lodgingData;

  const { checkInFullFormatted, checkOutFullFormatted } =
    useReservationDateTime(hotelReservation);

  const showRefundAmount =
    cancelScenario.LodgingCancelScenario ===
      LodgingCancelScenarioEnum.FullyRefundable ||
    cancelScenario.LodgingCancelScenario ===
      LodgingCancelScenarioEnum.PartiallyRefundable ||
    cancelScenario.LodgingCancelScenario ===
      LodgingCancelScenarioEnum.CfarRefundable;

  if (hotelData == null) {
    return null;
  }

  return (
    <div className={cn("container")}>
      <div className={cn("content")}>
        <div className={cn("info")}>
          <h2 className={cn("hotel-name")}>{hotelData.name}</h2>
          <div className={cn("hotel-meta")}>
            {hotelData.starRating && (
              <div className={cn("star-rating")}>
                <Icon iconName={IconNameEnum.starFilled} />
                <span>{toStarRating(hotelData.starRating)}</span>
              </div>
            )}
            <div className={cn("hotel-address")}>
              <Icon iconName={IconNameEnum.mapPin} />
              <span>{getLodgingAddress(hotelData)}</span>
            </div>
          </div>

          <div className={cn("date-info")}>
            <div className={cn("date-row")}>
              <span className={cn("date-label")}>
                {t("trips.tripCard.checkInDate")}
              </span>
              <span className={cn("date-value")}>{checkInFullFormatted}</span>
            </div>
            <div className={cn("date-row")}>
              <span className={cn("date-label")}>
                {t("trips.tripCard.checkOutDate")}
              </span>
              <span className={cn("date-value")}>{checkOutFullFormatted}</span>
            </div>
          </div>

          {showRefundAmount && (
            <div className={cn("refund-amount")}>
              <span className={cn("refund-label")}>
                {t("hotels.selfServe.cancel.estimatedRefundAmount")}
              </span>
              {cancelScenario.LodgingCancelScenario ===
              LodgingCancelScenarioEnum.CfarRefundable ? (
                <span className={cn("refund-value")}>
                  <PriceDisplay prices={cancelScenario.refundAmount} />
                </span>
              ) : cancelScenario.LodgingCancelScenario ===
                LodgingCancelScenarioEnum.PartiallyRefundable ? (
                <span className={cn("refund-value")}>
                  {cancelScenario.refundAmount}
                </span>
              ) : (
                <span className={cn("refund-value")}>
                  {[
                    refundAmount.fiat > 0 &&
                      formatFiatCurrency(
                        {
                          currencyCode:
                            hotelReservation.reservation.pricing?.currency ??
                            "USD",
                          value: refundAmount.fiat,
                        },
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                      ),
                    refundAmount.rewards > 0 &&
                      t("hotels.selfServe.cancel.pointsAmount", {
                        points: refundAmount.rewards,
                      }),
                  ]
                    .filter(Boolean)
                    .join(" + ")}
                </span>
              )}
            </div>
          )}
        </div>

        {hotelData.media && hotelData.media.length > 0 && (
          <div className={cn("image-container")}>
            <img
              src={hotelData.media[0]?.url}
              alt={hotelData.name}
              className={cn("image")}
            />
          </div>
        )}
      </div>
    </div>
  );
};
